/**
 * Dashboard API service functions
 */
import { apiClient } from '@/shared/utils/api';

export interface DashboardMetrics {
  total_patients: number;
  active_queue: number;
  completed_tests: number;
  pending_results: number;
}

export interface Assignment {
  id: string;
  patient_name: string;
  age: number;
  registration_time: string;
  token_number: string;
  doctor_name: string;
  department: string;
  status: 'waiting' | 'called' | 'inprogress' | 'completed';
  is_priority: boolean;
  estimated_wait_minutes: number;
}

export interface AssignmentsResponse {
  assignments: Assignment[];
}

export interface DepartmentRoom {
  room_name: string;
  room_number: string;
  status: 'active' | 'available';
  current_token: string;
  estimated_time: string;
  queue_count: number;
}

export interface DepartmentSummary {
  department_name: string;
  department_code: string;
  active_count: number;
  waiting_count: number;
  completed_count: number;
  rooms: DepartmentRoom[];
}

export interface DepartmentsResponse {
  departments: DepartmentSummary[];
}

export interface TestEntry {
  id: string;
  patient_name: string;
  patient_call_number?: string; // Reference to Active Queue call number (e.g., "#001")
  test_name: string;
  test_code: string;
  status: 'waiting' | 'called' | 'inprogress' | 'completed';
  priority_level: string;
  is_priority: boolean;
  queue_position: number;
  estimated_wait_minutes: number;
  technician: string;
  registration_time: string;
  room_name: string;
  room_number: string;
}

export interface RoomQueue {
  room_name: string;
  room_number: string;
  technician: string;
  test_entries: TestEntry[];
}

export interface DepartmentTestQueue {
  department_name: string;
  department_code: string;
  rooms: RoomQueue[];
}

export interface DepartmentQueuesResponse {
  department_queues: DepartmentTestQueue[];
  total_departments: number;
}

class DashboardService {
  async getMetrics(locationId?: string | null): Promise<DashboardMetrics> {
    const params: any = {};
    if (locationId && locationId !== 'all') {
      params.location_id = locationId;
    }
    const response = await apiClient.get('/dashboard/metrics', { params });
    return response.data;
  }

  async getAssignments(locationId?: string | null): Promise<AssignmentsResponse> {
    const params: any = {};
    if (locationId && locationId !== 'all') {
      params.location_id = locationId;
    }
    const response = await apiClient.get('/dashboard/assignments', { params });
    return response.data;
  }

  async getDepartments(locationId?: string | null): Promise<DepartmentsResponse> {
    const params: any = {};
    if (locationId && locationId !== 'all') {
      params.location_id = locationId;
    }
    const response = await apiClient.get('/dashboard/departments', { params });
    return response.data;
  }

  async getDepartmentQueues(locationId?: string | null): Promise<DepartmentQueuesResponse> {
    const params: any = {};
    if (locationId && locationId !== 'all') {
      params.location_id = locationId;
    }
    const response = await apiClient.get('/dashboard/department-queues', { params });
    return response.data;
  }

  async updateAssignmentPriority(assignmentId: string): Promise<{ success: boolean; is_priority: boolean }> {
    const response = await apiClient.patch(`/dashboard/assignments/${assignmentId}/priority`);
    return response.data;
  }
}

export const dashboardService = new DashboardService();